import { FormEvent, ReactNode, useState } from "react"
import { Head, useForm } from "@inertiajs/react"
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Text,
  TextField,
} from "@radix-ui/themes"
import { KeyRound, Loader2, Mail } from "lucide-react"

interface SignInProps {
  pending_email?: string | null
  was_otp_sent?: boolean
}

interface SignInForm {
  email: string
  code: string
}

function SignIn({ pending_email, was_otp_sent = false }: SignInProps) {
  const [wasOtpSent, setWasOtpSent] = useState(was_otp_sent)
  const { data, setData, post, processing, errors, clearErrors } =
    useForm<SignInForm>({
      email: pending_email ?? "",
      code: "",
    })

  function requestOtp(event: FormEvent) {
    event.preventDefault()
    clearErrors()
    post("/sign_in/request_otp", {
      preserveScroll: true,
      onSuccess: () => {
        setWasOtpSent(true)
        setData("code", "")
      },
    })
  }

  function verifyOtp(event: FormEvent) {
    event.preventDefault()
    clearErrors()
    post("/sign_in/verify_otp", { preserveScroll: true })
  }

  function useDifferentEmail() {
    clearErrors()
    setWasOtpSent(false)
    setData("code", "")
  }

  return (
    <Flex
      minHeight="100vh"
      align="center"
      justify="center"
      px="4"
      className="bg-[var(--sand-1)]"
    >
      <Head title="Sign in" />

      <Box width="100%" maxWidth="400px">
        <Card size="4">
          {wasOtpSent ? (
            <form onSubmit={verifyOtp}>
              <Flex direction="column" gap="5">
                <Box>
                  <Heading size="6" mb="2">
                    Check your email
                  </Heading>
                  <Text as="p" size="2" color="gray">
                    Enter the verification code sent to {data.email}.
                  </Text>
                </Box>

                <Flex direction="column" gap="2" asChild>
                  <label>
                    <Text size="2" weight="medium">
                      Verification code
                    </Text>
                    <TextField.Root
                      type="text"
                      required
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={data.code}
                      onChange={(event) =>
                        setData(
                          "code",
                          event.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                      placeholder="123456"
                    >
                      <TextField.Slot>
                        <KeyRound size={16} />
                      </TextField.Slot>
                    </TextField.Root>
                    {errors.code && (
                      <Text size="2" color="red">
                        {errors.code}
                      </Text>
                    )}
                  </label>
                </Flex>

                <Flex direction="column" gap="3">
                  <Button
                    type="submit"
                    variant="classic"
                    disabled={processing || data.code.length === 0}
                  >
                    {processing && <Loader2 size={16} className="animate-spin" />}
                    Verify
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    color="gray"
                    onClick={useDifferentEmail}
                  >
                    Use different email
                  </Button>
                </Flex>
              </Flex>
            </form>
          ) : (
            <form onSubmit={requestOtp}>
              <Flex direction="column" gap="5">
                <Box>
                  <Heading size="6" mb="2">
                    Sign in with your email
                  </Heading>
                  <Text as="p" size="2" color="gray">
                    We&apos;ll send you a verification code to confirm your email address.
                  </Text>
                </Box>

                <Flex direction="column" gap="2" asChild>
                  <label>
                    <Text size="2" weight="medium">
                      Email
                    </Text>
                    <TextField.Root
                      type="email"
                      required
                      autoComplete="email"
                      value={data.email}
                      onChange={(event) => setData("email", event.target.value)}
                      placeholder="hello@example.com"
                    >
                      <TextField.Slot>
                        <Mail size={16} />
                      </TextField.Slot>
                    </TextField.Root>
                    {errors.email && (
                      <Text size="2" color="red">
                        {errors.email}
                      </Text>
                    )}
                  </label>
                </Flex>

                <Button
                  type="submit"
                  variant="classic"
                  disabled={processing || data.email.trim().length === 0}
                >
                  {processing && <Loader2 size={16} className="animate-spin" />}
                  Continue
                </Button>
              </Flex>
            </form>
          )}
        </Card>
      </Box>
    </Flex>
  )
}

type InertiaPage = typeof SignIn & {
  layout?: (page: ReactNode) => ReactNode
}

const SignInPage = SignIn as InertiaPage
SignInPage.layout = (page) => page

export default SignInPage
